import { useEffect, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Loader,
  Modal,
  SegmentedControl,
  Select,
  TextInput,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconAlertTriangle } from '@tabler/icons-react'
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'
import { useClient, useOrpc, type OrpcClient } from '@/hooks/useClient'
import { formatPhone } from '@/utils.ts'
import { states } from '@/utils/states'
import { logError } from '@/utils/errorUtils'
import { formatPharmacyAddress, type StoredDefaultPharmacy } from './defaultPharmacy'

/** One directory row, typed straight off the contract — no local mirror to drift. */
type PharmacyRow = Awaited<ReturnType<OrpcClient['doseSpotPharmacySearch']>>['pharmacies'][number]

type SpecialtyFilter = 'All' | 'Retail' | 'MailOrder'

/**
 * The criteria snapshot a Search click submits. Pages are cached under a query key
 * derived from this snapshot — never the live fields — so criteria edited after a
 * search can't mix into "Load more" results for the previous query.
 */
type SearchQuery = {
  name?: string
  city?: string
  state?: string
  zip?: string
  phoneOrFax?: string
  specialty?: Exclude<SpecialtyFilter, 'All'>
}

type Props = {
  patientId: string
  /** The stored default being replaced, if any — drives the title and the confirm copy. */
  current?: StoredDefaultPharmacy
  onClose: () => void
  /** Fired after a successful save; the caller refetches the patient and closes. */
  onSaved: () => void
}

/**
 * The card's directory search (PD-1386, ticket 04 variant A): one view, explicit
 * Search button (no auto-search), the full verified field set, server-side
 * retail/mail-order filter, real 20-per-page paging surfaced as "Load more", and an
 * inline confirm strip on select. Search always proxies DoseSpot's directory —
 * there is no BeHuman pharmacy list.
 */
export const PharmacySearchModal = ({ patientId, current, onClose, onSaved }: Props) => {
  const client = useClient()
  const orpc = useOrpc()
  const [name, setName] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState<string | null>(null)
  const [zip, setZip] = useState('')
  const [phoneOrFax, setPhoneOrFax] = useState('')
  const [specialty, setSpecialty] = useState<SpecialtyFilter>('All')

  const [submitted, setSubmitted] = useState<SearchQuery | null>(null)
  const [selected, setSelected] = useState<PharmacyRow | null>(null)
  const [saving, setSaving] = useState(false)

  const query = useInfiniteQuery(
    orpc.doseSpotPharmacySearch.infiniteOptions({
      input: (pageNumber: number) => ({ ...submitted, ...(pageNumber > 1 && { pageNumber }) }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.hasNext ? allPages.length + 1 : undefined,
      enabled: submitted !== null,
      // The alert below carries its own Try again — fail fast, no background retries.
      retry: false,
      // Keep the visible rows while a re-search loads (no collapse to the loader).
      placeholderData: keepPreviousData,
    }),
  )

  useEffect(() => {
    if (query.error) logError('patient', 'DoseSpot pharmacy search', query.error)
  }, [query.error])

  const results = query.data ? query.data.pages.flatMap((page) => page.pharmacies) : null
  const totalCount = query.data?.pages.at(-1)?.totalCount ?? 0
  const searching = query.isFetching
  const failed = query.isError

  // Mirrors the API's documented ≥3-character minimums: every field the user has
  // typed into must carry 3+ non-space characters, so a half-typed term can't be sent
  // for DoseSpot to reject with a 400. A 2-letter state alone is still valid.
  const typedLengths = [name, city, zip, phoneOrFax].map((value) => value.replace(/\s/g, '').length)
  const canSearch =
    typedLengths.every((length) => length === 0 || length >= 3) &&
    (typedLengths.some((length) => length >= 3) || Boolean(state))

  const submitSearch = () => {
    setSelected(null)
    const next: SearchQuery = {
      ...(name.trim() && { name: name.trim() }),
      ...(city.trim() && { city: city.trim() }),
      ...(state && { state }),
      ...(zip.trim() && { zip: zip.trim() }),
      ...(phoneOrFax.trim() && { phoneOrFax: phoneOrFax.trim() }),
      ...(specialty !== 'All' && { specialty }),
    }
    // Re-submitting unchanged criteria keeps the same query key, so ask explicitly.
    if (submitted && JSON.stringify(next) === JSON.stringify(submitted)) void query.refetch()
    else setSubmitted(next)
  }

  const retryFailed = () => {
    setSelected(null)
    // A failed "Load more" retries the failed page; anything else re-runs the search.
    if (query.isFetchNextPageError) void query.fetchNextPage()
    else void query.refetch()
  }

  const saveSelected = async (pharmacy: PharmacyRow) => {
    setSaving(true)
    try {
      await client.doseSpotSetDefaultPharmacy({ patientId, ...pharmacy })
      showNotification({
        color: 'green',
        message: `${pharmacy.storeName} set as the default pharmacy.`,
      })
      onSaved()
    } catch (error) {
      logError('patient', 'Set default pharmacy', error)
      showNotification({
        color: 'red',
        message: error instanceof Error ? error.message : 'Unable to save the default pharmacy.',
      })
    } finally {
      setSaving(false)
    }
  }

  const remaining = results ? Math.max(0, Math.min(20, totalCount - results.length)) : 0

  return (
    <Modal
      opened
      onClose={onClose}
      size="640px"
      title={
        <span className="text-base font-semibold">
          {current ? 'Change default pharmacy' : 'Add default pharmacy'}
        </span>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (canSearch && !searching) submitSearch()
        }}
      >
        <div className="grid grid-cols-2 gap-2">
          <TextInput
            label="Pharmacy name"
            description="3+ characters"
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
          />
          <TextInput
            label="City"
            description="3+ characters"
            value={city}
            onChange={(event) => setCity(event.currentTarget.value)}
          />
          <Select
            label="State"
            data={states}
            value={state}
            onChange={setState}
            searchable
            clearable
          />
          <TextInput
            label="Zip"
            description="3+ characters"
            value={zip}
            onChange={(event) => setZip(event.currentTarget.value)}
          />
          <TextInput
            label="Phone or fax"
            description="3+ characters"
            value={phoneOrFax}
            onChange={(event) => setPhoneOrFax(event.currentTarget.value)}
          />
          <div className="flex flex-col justify-end gap-1">
            <SegmentedControl
              size="xs"
              value={specialty}
              onChange={(value) => setSpecialty(value as SpecialtyFilter)}
              data={[
                { label: 'All', value: 'All' },
                { label: 'Retail', value: 'Retail' },
                { label: 'Mail order', value: 'MailOrder' },
              ]}
            />
            <Button
              type="submit"
              size="sm"
              disabled={!canSearch}
              loading={searching && !query.isFetchingNextPage}
            >
              Search
            </Button>
          </div>
        </div>
      </form>

      {searching && !results && (
        <div className="flex flex-col items-center gap-2 py-10">
          <Loader size="sm" />
          <span className="text-sm text-gray-600">
            Searching DoseSpot&apos;s pharmacy directory…
          </span>
        </div>
      )}

      {failed && (
        <Alert
          color="orange"
          icon={<IconAlertTriangle size={18} />}
          className="mt-3"
          title="Search unavailable"
        >
          <div className="flex flex-col items-start gap-2">
            <span>
              Couldn&apos;t reach DoseSpot to search pharmacies. The saved default pharmacy is
              unaffected — try again in a moment.
            </span>
            <Button
              size="xs"
              variant="light"
              color="orange"
              loading={searching}
              onClick={retryFailed}
            >
              Try again
            </Button>
          </div>
        </Alert>
      )}

      {results && !selected && (
        <div className="mt-3">
          <div className="text-xs text-gray-500">
            {totalCount === 0
              ? 'No pharmacies matched — adjust the search and try again.'
              : `${totalCount} ${totalCount === 1 ? 'match' : 'matches'}`}
          </div>
          {results.map((pharmacy) => (
            <div
              key={pharmacy.pharmacyId}
              className="flex items-start justify-between gap-3 border-b border-gray-100 py-3 last:border-b-0"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{pharmacy.storeName}</span>
                  {pharmacy.specialties && pharmacy.specialties.length > 0 && (
                    <Badge size="xs" variant="light">
                      {pharmacy.specialties.join(', ')}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-gray-600">{formatPharmacyAddress(pharmacy)}</div>
                <div className="text-xs text-gray-500">
                  {formatPhone(pharmacy.phone)}
                  {pharmacy.fax ? ` · Fax ${formatPhone(pharmacy.fax)}` : ''}
                </div>
              </div>
              <Button size="xs" variant="light" onClick={() => setSelected(pharmacy)}>
                Select
              </Button>
            </div>
          ))}
          {query.hasNextPage && remaining > 0 && !query.isPlaceholderData && (
            <Button
              variant="subtle"
              size="xs"
              fullWidth
              className="mt-2"
              loading={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              Load {remaining} more
            </Button>
          )}
        </div>
      )}

      {selected && (
        <div className="mt-3 rounded-lg border border-[#D0E1F2] bg-[#F8FBFF] p-3">
          <p className="text-sm">
            Set <span className="font-semibold">{selected.storeName}</span> as the default pharmacy?
            {current ? ` (replaces ${current.storeName})` : ''}
          </p>
          <div className="mt-2 flex gap-2">
            <Button size="xs" loading={saving} onClick={() => void saveSelected(selected)}>
              Set as default
            </Button>
            <Button size="xs" variant="subtle" disabled={saving} onClick={() => setSelected(null)}>
              Back to results
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
